import type { InspectionEvidenceKind, InspectionMediaType, SorRecord } from '@circuitry-ai/doc-ui';

export type { InspectionEvidenceKind, InspectionMediaType };

/**
 * SOR definitions holding inspection templates and their checklist items. These are the
 * `x-ref` names from the schema; the API resolves the published qualified key at runtime
 * (see useInspectionTemplatesApi) and falls back to these when the lookup finds nothing.
 */
export const INSPECTION_TEMPLATE_DEFINITION = 'slm.inspection_template';
export const INSPECTION_TEMPLATE_ITEM_DEFINITION = 'slm.inspection_template_item';

/** `data` payload of a `slm.inspection_template` record */
export interface InspectionTemplateRecordData {
  name: string;
  templateKey: string;
  version?: number;
  locale?: string;
  tags?: string[];
  owner?: string | null;
  /** Shown as the template "Type" column (e.g. "Virtual Inspection") */
  category?: string | null;
  description?: string | null;
  instructions?: string | null;
  jurisdiction?: string | null;
  subjectKinds?: string[];
  effectiveFrom?: string | null;
  effectiveTo?: string | null;
  estimatedDurationMinutes?: number | null;
}

export type InspectionResponseType =
  | 'BOOLEAN'
  | 'TEXT'
  | 'LONG_TEXT'
  | 'INTEGER'
  | 'NUMBER'
  | 'DATE'
  | 'DATE_TIME'
  | 'SINGLE_CHOICE'
  | 'MULTI_CHOICE'
  | 'EVIDENCE';

export interface InspectionQuestion {
  responseType: InspectionResponseType;
  required?: boolean;
  criticality?: 'INFO' | 'LOW' | 'MEDIUM' | 'HIGH' | 'CRITICAL';
  evidencePolicy?: {
    allowedKind?: InspectionEvidenceKind;
    minimumCount?: number;
    maximumCount?: number | null;
    minimumPhotos?: number;
    commentRequired?: boolean;
    signatureRequired?: boolean;
  };
  // The schema has more question fields (options, limits, conditions...); this screen
  // doesn't edit them, but they are kept as-is when an item is saved.
  [key: string]: unknown;
}

/** `data` payload of a `slm.inspection_template_item` record */
export interface InspectionTemplateItemRecordData {
  templateId: string;
  itemKey: string;
  itemType: 'SECTION' | 'QUESTION';
  title: string;
  position?: number;
  tags?: string[];
  helpText?: string | null;
  description?: string | null;
  instructions?: string | null;
  parentItemId?: string | null;
  question?: InspectionQuestion;
}

export type InspectionTemplateRecord = SorRecord<InspectionTemplateRecordData>;
export type InspectionTemplateItemRecord = SorRecord<InspectionTemplateItemRecordData>;

/** One row of the Template Management grid */
export interface InspectionTemplate {
  id: string;
  revision: number;
  name: string;
  /** `data.category` */
  type: string;
  templateKey: string;
  /** null when the items couldn't be loaded */
  itemCount: number | null;
  requiredItemCount: number | null;
  record: InspectionTemplateRecord;
}

/** A checklist item as the editor shows it; `record` is unset for items not saved yet */
export interface InspectionTemplateItemDraft {
  /** Record id, or a temporary id for a new item */
  localId: string;
  title: string;
  mediaType: InspectionMediaType;
  required: boolean;
  record?: InspectionTemplateItemRecord;
}
